import Luff, {React} from "luff";
import {ButtonBusy, TextBox} from "luff-comp";
import WebApi from "api";

import {QRCodeGen} from "../../QRCodeGen/QRCodeGen";
import yandexKeyIcon from "./img/yandex-key.png";
import multifactorIcon from "./img/multifactor.png";
import "./TwoFactorMan.scss";

export type TwoFactorCallback = (success: boolean, authPack?: WebApi.AuthPack) => void;
export type TwoFactorSetupCallback = (success: boolean, authPack?: WebApi.AuthPack) => void;
export type TwoFactorCancelCallback = () => void;

type TTwoFactorMode = "verify" | "setup";

export class TwoFactorMan extends Luff.Content {
    IsOpen = Luff.State(false);

    private Mode = Luff.State<TTwoFactorMode>("verify");
    private TwoFactorType = Luff.State(WebApi.TwoFactorType.TOTP);
    private TotpAuthUri = Luff.State("");
    private TotpCode = Luff.State("");
    private IsSetupReady = Luff.State(false);
    private IsLoading = Luff.State(false);
    private IsOrgRequired = Luff.State(false);
    private CancelCaption = Luff.State("← Назад к входу");

    private _callback: TwoFactorCallback | null = null;
    private _setupCallback: TwoFactorSetupCallback | null = null;
    private _onCancel: TwoFactorCancelCallback | null = null;

    Run(type: WebApi.TwoFactorType, callback: TwoFactorCallback, onCancel?: TwoFactorCancelCallback) {
        this.TwoFactorType.SValue = type;
        this._callback = callback;
        this._setupCallback = null;
        this._onCancel = onCancel ?? null;
        this.Mode.SValue = "verify";
        this.IsOrgRequired.SValue = false;
        this.CancelCaption.SValue = "← Назад к входу";
        this.ResetFields();
        this.Open();
    }

    RunSetup(callback?: TwoFactorSetupCallback, onCancel?: TwoFactorCancelCallback, isOrgRequired = false, cancelCaption = "← Назад к входу") {
        this._setupCallback = callback ?? null;
        this._callback = null;
        this._onCancel = onCancel ?? null;
        this.Mode.SValue = "setup";
        this.TwoFactorType.SValue = WebApi.TwoFactorType.TOTP;
        this.IsOrgRequired.SValue = isOrgRequired;
        this.CancelCaption.SValue = cancelCaption;
        this.ResetFields();
        this.Open();
        this.LoadSetup();
    }

    Open() {
        this.IsOpen.SValue = true;
    }

    Close() {
        this.IsOpen.SValue = false;
    }

    private ResetFields() {
        this.TotpCode.SValue = "";
        this.TotpAuthUri.SValue = "";
        this.IsSetupReady.SValue = false;
        this.IsLoading.SValue = false;
    }

    private LoadSetup() {
        this.IsLoading.SValue = true;
        return WebApi.TOTP_Setup()
            .then(data => {
                if (!data?.OtpAuthUri) {
                    Luff.Pop.Error("Не удалось начать настройку 2FA");
                    this.Finish(false);
                    return;
                }
                this.TotpAuthUri.SValue = data.OtpAuthUri;
                this.IsSetupReady.SValue = true;
            })
            .catch(() => {
                Luff.Pop.Error("Не удалось начать настройку 2FA");
                this.Finish(false);
            })
            .finally(() => {
                this.IsLoading.SValue = false;
            });
    }

    private Submit() {
        const code = this.TotpCode.SValue.trim();
        if (code.length < 6) {
            Luff.Pop.Error("Введите 6-значный код");
            return Promise.reject();
        }

        if (this.Mode.SValue === "setup") {
            return this.FinishSetup(code);
        }
        return this.Verify(code);
    }

    private FinishSetup(code: string) {
        return WebApi.TOTP_SetupFinish(code)
            .then(res => {
                if (!res.IsValid) {
                    Luff.Pop.Error(res.Message || "Неверный код");
                    this._setupCallback?.(false);
                    return;
                }

                return this.AuthenticateTwoFactor(code)
                    .then(authPack => {
                        if (!authPack) {
                            this._setupCallback?.(false);
                            return;
                        }
                        Luff.Pop.Success(res.Message || "Двухфакторная аутентификация подключена");
                        this._setupCallback?.(true, authPack);
                        this.Close();
                    });
            })
            .catch(() => {
                Luff.Pop.Error("Не удалось завершить настройку 2FA");
                this._setupCallback?.(false);
            });
    }

    private AuthenticateTwoFactor(code: string): Promise<WebApi.AuthPack | null> {
        return WebApi.Login_AuthenticateTwoFactor(code)
            .then(res => {
                if (res.Error > 0 || !res.Result?.IsTwoFaPassed) {
                    Luff.Pop.Error("Неверный код");
                    return null;
                }
                return res.Result;
            })
            .catch(() => {
                Luff.Pop.Error("Не удалось проверить код");
                return null;
            });
    }

    private Verify(code: string) {
        const type = this.TwoFactorType.SValue;

        if (type === WebApi.TwoFactorType.TOTP) {
            return this.AuthenticateTwoFactor(code)
                .then(authPack => {
                    if (!authPack) {
                        this._callback?.(false);
                        return;
                    }
                    this._callback?.(true, authPack);
                    this.Close();
                });
        }

        if (type === WebApi.TwoFactorType.Email) {
            Luff.Pop.Error("Подтверждение по email пока не поддерживается");
            this._callback?.(false);
            return Promise.reject();
        }

        Luff.Pop.Error("Неизвестный тип двухфакторной аутентификации");
        this._callback?.(false);
        return Promise.reject();
    }

    private Finish(success: boolean) {
        if (!success) {
            this._onCancel?.();
        }

        if (this.Mode.SValue === "setup") {
            this._setupCallback?.(success);
            this.Close();
            return;
        }

        this._callback?.(success);
        this.Close();
    }

    private OnKeyUp(e: Luff.KeyboardEvent) {
        if (e.which === 13) {
            this.Submit();
        }
    }

    Render(): Luff.Node {
        const isSetup = this.Mode.SubState(mode => mode === "setup");
        const showQr = this.TotpAuthUri.SubState(
            uri => this.Mode.SValue === "setup" && uri.length > 0,
            [this.Mode],
        );
        const showCode = this.Mode.SubState(
            mode => mode !== "setup" || this.IsSetupReady.SValue,
            [this.IsSetupReady],
        );
        const isSubmitDisabled = this.Mode.SubState(
            mode => mode === "setup" && (this.IsLoading.SValue || !this.IsSetupReady.SValue),
            [this.IsLoading, this.IsSetupReady],
        );
        const caption = this.Mode.SubState(mode => mode === "setup"
            ? "Настройка двухфакторной аутентификации"
            : "Подтверждение входа");
        const hint = this.Mode.SubState(mode => mode === "setup"
            ? "Отсканируйте QR-код в приложении-аутентификаторе и введите полученный код."
            : "Введите код из приложения-аутентификатора.");
        const submitCaption = this.Mode.SubState(mode => mode === "setup" ? "Подтвердить" : "Войти");

        return (
            <div className="two-factor-man" isVisible={this.IsOpen}>
                <div className="two-factor-man__org-notice" isVisible={this.IsOrgRequired}>
                    Ваша организация включила двухфакторную авторизацию
                </div>

                <button className="two-factor-man__back" type="button" onClick={() => this.Finish(false)}>
                    {this.CancelCaption}
                </button>

                <div className="two-factor-man__title">{caption}</div>
                <div className="two-factor-man__hint">{hint}</div>

                <div className="two-factor-man__apps" isVisible={isSetup}>
                    <div className="two-factor-man__apps-label">Рекомендуемые приложения:</div>
                    <div className="two-factor-man__apps-list">
                        <a
                            className="two-factor-man__app-btn"
                            href="https://www.rustore.ru/catalog/app/ru.yandex.key"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img className="two-factor-man__app-icon" src={yandexKeyIcon} alt="" />
                            <span className="two-factor-man__app-name">Яндекс Ключ</span>
                        </a>
                        <a
                            className="two-factor-man__app-btn"
                            href="https://www.rustore.ru/catalog/app/ru.multifactor.app"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img className="two-factor-man__app-icon" src={multifactorIcon} alt="" />
                            <span className="two-factor-man__app-name">Multifactor</span>
                        </a>
                    </div>
                </div>

                <div className="two-factor-man__qr" isVisible={showQr}>
                    <QRCodeGen str={this.TotpAuthUri} />
                </div>

                <label isVisible={showCode}>
                    <span className="two-factor-man__label">Код из приложения</span>
                    <TextBox
                        className="textbox-totp"
                        value={this.TotpCode}
                        placeholder="000000"
                        disabled={isSubmitDisabled}
                        onKeyUp={(e) => this.OnKeyUp(e)}
                    />
                </label>

                <ButtonBusy
                    onClick={() => this.Submit()}
                    disabled={isSubmitDisabled}
                >
                    {submitCaption}
                </ButtonBusy>
            </div>
        );
    }
}
